/**
 * Firestore room helpers — create / join / observe / mutate rooms.
 *
 * Data model (Firestore):
 *   /rooms/{roomCode}             — GameRoom document
 *   /rooms/{roomCode}/turns/{idx} — Turn document per turn
 *
 * The room code is a human-friendly 6-letter id used as the document id so
 * a single read by code resolves the room without a query.
 */

import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  type Unsubscribe,
} from "firebase/firestore";
import { signInAnonymously, onAuthStateChanged } from "firebase/auth";
import { getFirebase } from "../firebase";
import type { GameRoom, Player, Turn } from "./types";
import type { Lang } from "../i18n";

function makeCode(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ"; // no I/O for legibility
  let s = "";
  for (let i = 0; i < 6; i++) s += alphabet[Math.floor(Math.random() * alphabet.length)];
  return s;
}

async function ensureAuth(): Promise<string> {
  const { auth } = getFirebase();
  if (auth.currentUser) return auth.currentUser.uid;
  await signInAnonymously(auth);
  return new Promise((resolve, reject) => {
    const unsub = onAuthStateChanged(auth, (u) => {
      unsub();
      if (u) resolve(u.uid);
      else reject(new Error("Anonymous sign-in failed"));
    });
  });
}

export async function createRoom(opts: {
  displayName: string;
  language: Lang;
  interpretSeconds?: number | null;
  strictness?: "strict" | "normal" | "easy";
}): Promise<{ roomCode: string }> {
  const uid = await ensureAuth();
  const { db } = getFirebase();
  // Try a few codes until one is free
  for (let attempt = 0; attempt < 6; attempt++) {
    const code = makeCode();
    const ref = doc(db, "rooms", code);
    const snap = await getDoc(ref);
    if (snap.exists()) continue;
    const player: Player = {
      id: uid,
      displayName: opts.displayName,
      score: 0,
      joinedAt: Date.now(),
    };
    const room: GameRoom = {
      id: code,
      code,
      hostId: uid,
      language: opts.language,
      phase: "lobby",
      players: { [uid]: player },
      turnOrder: [uid],
      currentTurnIndex: 0,
      rounds: 3,
      scoreLimit: 50,
      interpretSeconds: opts.interpretSeconds === undefined ? 60 : opts.interpretSeconds,
      strictness: opts.strictness ?? "normal",
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    await setDoc(ref, { ...room, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    return { roomCode: code };
  }
  throw new Error("Could not allocate a room code. Try again.");
}

export async function joinRoom(opts: { roomCode: string; displayName: string }): Promise<void> {
  const uid = await ensureAuth();
  const { db } = getFirebase();
  const ref = doc(db, "rooms", opts.roomCode);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`No room with code ${opts.roomCode}.`);
  const room = snap.data() as GameRoom;
  if (room.players[uid]) return; // already joined
  if (room.phase !== "lobby") throw new Error("This room is already in progress.");
  const player: Player = {
    id: uid,
    displayName: opts.displayName,
    score: 0,
    joinedAt: Date.now(),
  };
  await updateDoc(ref, {
    [`players.${uid}`]: player,
    turnOrder: [...room.turnOrder, uid],
    updatedAt: serverTimestamp(),
  });
}

/**
 * Subscribe to a Firestore snapshot with automatic recovery.
 *
 * Without an error handler, a single dropped listen (e.g. the brief window
 * where the anonymous auth token hasn't yet propagated to the Firestore
 * channel, yielding a one-off permission-denied) kills the listener for good —
 * the screen then shows stale data until the user manually refreshes. That's
 * the bug behind "other players have to refresh to see the host start the
 * game." Re-subscribing on error lets live updates reach every client without
 * a manual refresh.
 */
function watchWithRecovery<T>(
  makeRef: () => ReturnType<typeof doc> | ReturnType<typeof collection>,
  onData: (snap: any) => void,
  label: string,
): Unsubscribe {
  let active = true;
  let inner: Unsubscribe = () => {};
  const subscribe = () => {
    inner = onSnapshot(
      makeRef() as any,
      (snap: any) => onData(snap),
      (err: unknown) => {
        console.warn(`[${label}] listener error — resubscribing`, err);
        try { inner(); } catch { /* already detached */ }
        if (active) setTimeout(() => { if (active) subscribe(); }, 1200);
      },
    );
  };
  subscribe();
  return () => { active = false; inner(); };
}

export function watchRoom(roomCode: string, cb: (room: GameRoom | null) => void): Unsubscribe {
  const { db } = getFirebase();
  return watchWithRecovery(
    () => doc(db, "rooms", roomCode),
    (snap) => cb(snap.exists() ? (snap.data() as GameRoom) : null),
    "watchRoom",
  );
}

export function watchTurns(roomCode: string, cb: (turns: Turn[]) => void): Unsubscribe {
  const { db } = getFirebase();
  return watchWithRecovery(
    () => collection(db, "rooms", roomCode, "turns"),
    (snap) => cb(snap.docs.map((d: any) => d.data() as Turn).sort((a: Turn, b: Turn) => a.index - b.index)),
    "watchTurns",
  );
}

export async function updateRoom(roomCode: string, patch: Partial<GameRoom>): Promise<void> {
  const { db } = getFirebase();
  await updateDoc(doc(db, "rooms", roomCode), { ...patch, updatedAt: serverTimestamp() });
}

export async function writeTurn(roomCode: string, turn: Turn): Promise<void> {
  const { db } = getFirebase();
  await setDoc(doc(db, "rooms", roomCode, "turns", String(turn.index)), turn);
}

/** Submit one player's interpretation without overwriting others. */
export async function submitInterpretation(
  roomCode: string,
  turnIndex: number,
  uid: string,
  text: string,
): Promise<void> {
  const { db } = getFirebase();
  await updateDoc(doc(db, "rooms", roomCode, "turns", String(turnIndex)), {
    [`interpretations.${uid}`]: text,
  });
}

/** Atomically: write turn pointsAwarded, increment each player's score on the room, and switch phase to reveal. */
export async function revealAndScore(
  roomCode: string,
  turnIndex: number,
  points: Record<string, number>,
  newScores: Record<string, number>,
): Promise<void> {
  const { db } = getFirebase();
  const batch = writeBatch(db);
  const turnRef = doc(db, "rooms", roomCode, "turns", String(turnIndex));
  batch.update(turnRef, { pointsAwarded: points, revealedAt: Date.now() });
  const roomRef = doc(db, "rooms", roomCode);
  const playerUpdates: Record<string, unknown> = { phase: "reveal", updatedAt: serverTimestamp() };
  for (const [uid, score] of Object.entries(newScores)) {
    playerUpdates[`players.${uid}.score`] = score;
  }
  batch.update(roomRef, playerUpdates);
  await batch.commit();
}

export async function getCurrentUid(): Promise<string> {
  return ensureAuth();
}
